import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { mockData } from '../mock';
import { apiService } from '../services/api';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from './LoadingSpinner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { success, error } = useToast();
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await apiService.submitContact(formData);
      success(response.message);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnrollment = async () => {
    setIsSubmitting(true);
    
    try {
      const enrollmentData = {
        name: formData.name || 'Quick Enrollment',
        email: formData.email || '',
        phone: formData.phone || '',
        course_interest: 'Battery Pack Building Course',
        experience_level: 'Beginner'
      };
      
      if (!enrollmentData.email) {
        error('Please provide an email address to enroll');
        setIsSubmitting(false);
        return;
      }
      
      const response = await apiService.enrollCourse(enrollmentData);
      success(response.message);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section id="contact" className="pad-2xl">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="heading-1 mb-4">Ready to Start Learning?</h2>
          <p className="body-large max-w-2xl mx-auto">
            Get in touch to enroll in our battery pack building course or 
            ask any questions about the curriculum.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div>
            <div className="voice-card accent-blue mb-6">
              <h3 className="heading-3 mb-6">Contact Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="body-medium font-medium">Email</div>
                    <div className="body-small text-muted">{mockData.contact.email}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="body-medium font-medium">Phone</div>
                    <div className="body-small text-muted">{mockData.contact.phone}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="body-medium font-medium">Address</div>
                    <div className="body-small text-muted">{mockData.contact.address}</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Quick Enrollment */}
            <div className="voice-card accent-orange">
              <h3 className="heading-3 mb-4">Quick Enrollment</h3>
              <p className="body-medium mb-6">
                Ready to start immediately? Click below to secure your spot in the next batch.
              </p>
              <button 
                onClick={handleEnrollment}
                disabled={isSubmitting}
                className="btn-primary w-full hover-scale relative"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="small" className="mr-2" />
                    Processing...
                  </>
                ) : (
                  <>Enroll Now - {mockData.enrollment.price}</>
                )}
              </button>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="voice-card accent-grey">
            <h3 className="heading-3 mb-6">Send us a Message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="body-medium font-medium block mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border border-input rounded-lg focus:border-input-focus focus:outline-none transition-colors"
                  placeholder="Enter your full name"
                />
              </div>
              
              <div>
                <label className="body-medium font-medium block mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border border-input rounded-lg focus:border-input-focus focus:outline-none transition-colors"
                  placeholder="Enter your email"
                />
              </div>
              
              <div>
                <label className="body-medium font-medium block mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full p-3 border border-input rounded-lg focus:border-input-focus focus:outline-none transition-colors"
                  placeholder="Enter your phone number"
                />
              </div>
              
              <div>
                <label className="body-medium font-medium block mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full p-3 border border-input rounded-lg focus:border-input-focus focus:outline-none transition-colors resize-vertical"
                  placeholder="Tell us about your interest in battery pack building..."
                />
              </div>
              
              <button 
                type="submit" 
                disabled={isSubmitting}
                className="btn-primary w-full hover-scale relative"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="small" className="mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;